import { cn } from '@/lib/utils';
import { FOCUS_RING } from '@/shared/components/pillClasses';
import type { TaskLabelView } from '@/lib/api/generated/model';

import { type LabelChipDensity } from '../chipShape';
import { LabelChip } from './LabelChip';

export interface NarrowingLabelChipProps {
  label: Pick<TaskLabelView, 'id' | 'name' | 'color'>;
  /** Narrow the surrounding surface to this label. */
  onNarrow: (labelId: string) => void;
  /**
   * Whether pressing this chip must stop the event reaching what is behind it.
   *
   * Both answers are correct somewhere, which is exactly why the caller states
   * it. A chip inside a row that is itself a link has to swallow the click or
   * the page navigates instead of filtering; a chip on a board card must *not*,
   * because the card's click is the only way into the task.
   */
  stopPropagation?: boolean;
  density?: LabelChipDensity;
  /**
   * Shrink to the room there is, ending the name in an ellipsis, with the full
   * name on hover. For a cell narrower than a long label, like the Tasks page's
   * Labels column; everywhere else a chip keeps its full width.
   */
  truncate?: boolean;
}

/**
 * A label you can press to see everything else wearing it, and nothing more.
 *
 * The third and last of this package's three chips, and the reason they are
 * three named components rather than one component with four undocumented
 * behaviours: what a chip does was decided by which file rendered it, and all
 * four looked identical.
 *
 * - `LabelChip` — inert. A board card is already a drag handle and a way into
 *   the task; a third target on it makes both unreliable.
 * - `NarrowingLabelChip` — this one. Pressing it filters the surface it is on.
 * - `RemovableLabelChip` — carries a `×`, and narrows too where narrowing means
 *   anything.
 *
 * A caller picks by what its surface can actually do to a label, and the name
 * says which it picked.
 */
export function NarrowingLabelChip({
  label,
  onNarrow,
  stopPropagation = false,
  density = 'card',
  truncate = false,
}: NarrowingLabelChipProps) {
  return (
    <button
      type="button"
      aria-label={`Show all tasks labelled ${label.name}`}
      onClick={(event) => {
        if (stopPropagation) {
          event.preventDefault();
          event.stopPropagation();
        }
        onNarrow(label.id);
      }}
      title={truncate ? label.name : undefined}
      className={cn(
        'rounded-4xl transition-opacity hover:opacity-80',
        truncate ? 'flex min-w-0' : 'shrink-0',
        FOCUS_RING,
      )}
    >
      <LabelChip
        label={label}
        density={density}
        className={truncate ? 'max-w-full shrink' : undefined}
      />
    </button>
  );
}
